import type { PlotSeries } from "@/shared/state/visualization";
import { seriesConfig } from "@/shared/state/visualization/identifiers";
import { nanoSecToSecLossy } from "@/shared/time";

import type { SeriesAlignment } from "./alignment";
import { type ChartXGeometry, domainXToPixel } from "./chartXDomainMapping";
import type { LogTimeBounds } from "./PlotDataStreamer";

export interface PlayheadPosition {
  /**
   * The recording and basis the line marks; series sharing both collapse into
   * one line. Stable across renders, so TimelineIndicator uses it as the
   * line's React key.
   */
  id: string;
  /** Horizontal pixel position within the panel. */
  left: number;
  /**
   * The line color of the series the position came from, so the playhead
   * matches the recording it tracks. Undefined when the series has no
   * configured color, leaving the indicator its stylesheet color.
   */
  color: string | undefined;
}

/**
 * The slice of a chart's extents the absolute playhead reads: the visible
 * x-axis window in absolute log time, and the chart area's pixel origin and
 * width. Structurally satisfied by the `Extents` type from PlotRenderer.
 */
interface AbsolutePlayheadChartExtents {
  data: { viewportLogTime: LogTimeBounds };
  pixel: { left: number; width: number };
}

/**
 * Where an absolute-time panel draws its playhead: the horizontal pixel
 * position of a time within the chart's visible window, or null when the time
 * falls outside that window. A zero-width window pins the playhead to the
 * chart area's left edge, whereas {@link playheadPositions} draws nothing on
 * degenerate geometry.
 */
export function absolutePlayheadPixel(
  timeNs: bigint,
  chartExtents: AbsolutePlayheadChartExtents,
): number | null {
  const {
    data: {
      viewportLogTime: { min, max },
    },
    pixel: { left, width },
  } = chartExtents;
  if (timeNs < min || timeNs > max) {
    return null;
  }
  const fraction = Number(timeNs - min) / Number(max - min);
  if (!Number.isFinite(fraction)) {
    // Number.isFinite catches the NaN a zero-width window's 0/0 produces.
    return left;
  }
  return left + fraction * width;
}

interface PlayheadPositionsParams extends ChartXGeometry {
  series: PlotSeries[];
  alignment: SeriesAlignment;
  /** The timer's current absolute time. */
  timeNs: bigint;
}

/**
 * Where an aligned panel draws its playhead: one vertical line per recording
 * whose span contains the timer's current time, placed at that recording's
 * elapsed x. Recordings that do not overlap in absolute time hold the playhead
 * one at a time, so a single tinted line moves from one to the next;
 * recordings that do overlap each draw their own. Within one recording, series
 * with different bases draw a line each.
 */
export function playheadPositions({
  series,
  alignment,
  timeNs,
  viewportDomain,
  pixel,
}: PlayheadPositionsParams): PlayheadPosition[] {
  const positions: PlayheadPosition[] = [];
  const seen = new Set<string>();
  for (const plotSeries of series) {
    if (!plotSeries.visible) {
      continue;
    }
    const aligned = alignment.seriesById[plotSeries.id];
    if (aligned === undefined) {
      continue;
    }
    if (timeNs < aligned.sourceStartTime || timeNs > aligned.sourceEndTime) {
      continue;
    }
    // The same source and basis give the same elapsed x, so the first visible
    // series stands in for the rest.
    const id = `${aligned.sourceId}@${aligned.basis}`;
    if (seen.has(id)) {
      continue;
    }
    seen.add(id);
    const x = nanoSecToSecLossy(timeNs - aligned.basis);
    if (x < viewportDomain.min || x > viewportDomain.max) {
      continue;
    }
    const left = domainXToPixel(x, { viewportDomain, pixel });
    if (left === undefined) {
      // A viewport or chart area of zero or negative width has no pixel for
      // any time.
      continue;
    }
    positions.push({ id, left, color: seriesConfig(plotSeries)?.lineColor });
  }
  return positions;
}
